import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Plus, Edit, Trash2, ArrowLeft, Search, MoreHorizontal, Mail, Phone,
  Building, Calendar, DollarSign, Loader2, UserPlus, UserCheck, UserX, Clock, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { format, parseISO } from "date-fns";

export default function ClientsPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    address: "",
    tax_id: "",
    status: "active",
    notes: "",
    tags: []
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [clientsRes, statsRes] = await Promise.all([
        api.get("/clients"),
        api.get("/clients/stats")
      ]);
      setClients(clientsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      if (error.response?.status === 403) {
        toast.error("This feature is only available for business accounts");
        navigate("/dashboard");
      } else {
        toast.error(t("failed_load_data", "dashboard") || "Failed to load data");
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openCreateDialog();
    }
  }, [searchParams]);

  const openCreateDialog = () => {
    setEditingClient(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      company_name: "",
      address: "",
      tax_id: "",
      status: "active",
      notes: "",
      tags: []
    });
    setShowDialog(true);
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || "",
      phone: client.phone || "",
      company_name: client.company_name || "",
      address: client.address || "",
      tax_id: client.tax_id || "",
      status: client.status,
      notes: client.notes || "",
      tags: client.tags || []
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a client name");
      return;
    }

    setIsSaving(true);
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, formData);
        toast.success(t("client_updated", "company") || "Client updated successfully");
      } else {
        await api.post("/clients", formData);
        toast.success(t("client_created", "company") || "Client created successfully");
      }
      
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/clients/${deleteConfirm.id}`);
      toast.success(t("client_deleted", "company") || "Client deleted successfully");
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "active": return { label: t("active", "common") || "Active", variant: "default", icon: UserCheck };
      case "inactive": return { label: t("inactive", "common") || "Inactive", variant: "secondary", icon: UserX };
      case "prospect": return { label: t("prospect", "company") || "Prospect", variant: "outline", icon: UserPlus };
      case "churned": return { label: t("churned", "company") || "Churned", variant: "destructive", icon: UserX };
      default: return { label: status, variant: "outline", icon: Users };
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (client.email && client.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (client.company_name && client.company_name.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === "all" || client.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="max-w-[1200px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </header>
        <main className="max-w-[1200px] mx-auto px-4 py-6">
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="clients-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-[1200px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                {t("clients", "company") || "Clients"}
              </h1>
              <p className="text-xs text-muted-foreground">{clients.length} {t("total", "common") || "total"}</p>
            </div>
          </div>
          
          <Button onClick={openCreateDialog} data-testid="add-client-button">
            <Plus className="h-4 w-4 mr-2" />
            {t("add_client", "company") || "Add Client"}
          </Button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 py-6 space-y-4">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_clients}</p>
                  <p className="text-xs text-muted-foreground">{t("total_clients", "company") || "Total Clients"}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <UserCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active_clients}</p>
                  <p className="text-xs text-muted-foreground">{t("active_clients", "company") || "Active"}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                  <UserPlus className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.prospects}</p>
                  <p className="text-xs text-muted-foreground">{t("prospects", "company") || "Prospects"}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                  <Clock className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.new_this_month}</p>
                  <p className="text-xs text-muted-foreground">{t("new_this_month", "company") || "New This Month"}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("search_clients", "company") || "Search clients..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_statuses", "company") || "All Statuses"}</SelectItem>
              <SelectItem value="active">{t("active", "common") || "Active"}</SelectItem>
              <SelectItem value="inactive">{t("inactive", "common") || "Inactive"}</SelectItem>
              <SelectItem value="prospect">{t("prospect", "company") || "Prospect"}</SelectItem>
              <SelectItem value="churned">{t("churned", "company") || "Churned"}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Clients Table */}
        <Card>
          <CardContent className="p-0">
            {filteredClients.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery ? (t("no_clients_found", "company") || "No clients match your search") : (t("no_clients_yet", "company") || "No clients yet")}
                </p>
                {!searchQuery && (
                  <Button className="mt-4" onClick={openCreateDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("add_first_client", "company") || "Add Your First Client"}
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("name", "common") || "Name"}</TableHead>
                    <TableHead>{t("contact", "company") || "Contact"}</TableHead>
                    <TableHead>{t("company", "company") || "Company"}</TableHead>
                    <TableHead>{t("purchases", "company") || "Purchases"}</TableHead>
                    <TableHead>{t("revenue", "company") || "Revenue"}</TableHead>
                    <TableHead>{t("status", "common") || "Status"}</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => {
                    const statusConfig = getStatusConfig(client.status);
                    const StatusIcon = statusConfig.icon;
                    return (
                      <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{client.name}</p>
                            {client.last_purchase_date && (
                              <p className="text-xs text-muted-foreground">
                                {t("last_purchase", "company") || "Last:"} {format(parseISO(client.last_purchase_date), "MMM d, yyyy")}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {client.email && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Mail className="h-3 w-3" />
                                {client.email}
                              </div>
                            )}
                            {client.phone && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                {client.phone}
                              </div>
                            )}
                            {client.tax_id && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <FileText className="h-3 w-3" />
                                {t("tax_id", "company") || "Tax ID"}: {client.tax_id}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {client.company_name ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Building className="h-3 w-3 text-muted-foreground" />
                              {client.company_name}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{client.total_purchases || 0}</TableCell>
                        <TableCell className="font-medium">{formatCurrency(client.total_revenue)}</TableCell>
                        <TableCell>
                          <Badge variant={statusConfig.variant} className="gap-1">
                            <StatusIcon className="h-3 w-3" />
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`client-menu-${client.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/sales?client_id=${client.id}`)}>
                                <DollarSign className="h-4 w-4 mr-2" />
                                {t("view_sales", "company") || "View Sales"}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditDialog(client)}>
                                <Edit className="h-4 w-4 mr-2" />
                                {t("edit", "common") || "Edit"}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-destructive" 
                                onClick={() => setDeleteConfirm(client)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {t("delete", "common") || "Delete"}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? (t("edit_client", "company") || "Edit Client") : (t("add_client", "company") || "Add Client")}
            </DialogTitle>
            <DialogDescription>
              {editingClient ? (t("edit_client_desc", "company") || "Update client details") : (t("add_client_desc", "company") || "Add a new client to your database")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>{t("name", "common") || "Name"} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder={t("client_name_placeholder", "company") || "Enter client name"}
                data-testid="client-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("email", "common") || "Email"}</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@example.com"
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("phone", "common") || "Phone"}</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1 234 567 890"
                  data-testid="client-phone-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t("company_name", "company") || "Company Name"}</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                placeholder={t("company_name_placeholder", "company") || "Client's company (optional)"}
                data-testid="client-company-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t("address", "company") || "Address"}</Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder={t("address_placeholder", "company") || "Enter address (optional)"}
                rows={2}
                data-testid="client-address-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t("tax_id", "company") || "Tax ID"}</Label>
              <Input
                value={formData.tax_id}
                onChange={(e) => setFormData({...formData, tax_id: e.target.value})}
                placeholder={t("tax_id_placeholder", "company") || "Tax identification number (optional)"}
                data-testid="client-tax-id-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t("status", "common") || "Status"}</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                <SelectTrigger data-testid="client-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t("active", "common") || "Active"}</SelectItem>
                  <SelectItem value="inactive">{t("inactive", "common") || "Inactive"}</SelectItem>
                  <SelectItem value="prospect">{t("prospect", "company") || "Prospect"}</SelectItem>
                  <SelectItem value="churned">{t("churned", "company") || "Churned"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t("notes", "common") || "Notes"}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder={t("notes_placeholder", "company") || "Add notes about this client (optional)"}
                rows={3}
                data-testid="client-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={isSaving}>
              {t("cancel", "common") || "Cancel"}
            </Button>
            <Button onClick={handleSave} disabled={isSaving} data-testid="save-client-button">
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingClient ? (t("update", "common") || "Update") : (t("create", "common") || "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete", "common") || "Confirm Delete"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete_client_warning", "company") || "Are you sure you want to delete"} "{deleteConfirm?.name}"? {t("action_irreversible", "common") || "This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel", "common") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("delete", "common") || "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
